import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Resets scroll position when the route changes.
 *
 * React Router keeps the window scroll offset across navigations, so following
 * a footer link while scrolled to the bottom of a long page landed the reader
 * part-way down the next one. Hash links are left alone: those are in-page
 * anchors (the homepage sections) that need to keep their target offset.
 *
 * Distinct from ScrollToTop, which renders the floating "back to top" button.
 */
const ScrollToTopOnNavigate = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Anchors arriving from another route (e.g. the header's "Features" link
      // pointing at /#features) need scrolling once the target has rendered.
      const id = hash.slice(1);
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top, left: 0, behavior: 'smooth' });
          return true;
        }
        return false;
      };
      if (scrollToTarget()) return;
      // The homepage lazy-loads its sections, so retry briefly before giving up.
      let tries = 0;
      const timer = window.setInterval(() => {
        if (scrollToTarget() || ++tries > 20) window.clearInterval(timer);
      }, 100);
      return () => window.clearInterval(timer);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash]);

  return null;
};

export default ScrollToTopOnNavigate;
